import type React from "react";
import { useMemo } from "react";
import { TableCell, TableRow, Typography } from "@mui/material";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import { useTranslation } from "react-i18next";
import DetailSectionCard from "../common/DetailSectionCard";
import Table from "../common/Table";
import { TestId } from "../../constants/testIds";
import type translations from "../../translations/translations.json";
import type { DashboardSeriesPoint } from "../../types/dashboard";
import { formatRupiah } from "../../utils/formatRupiah";

type TranslationKeys = keyof typeof translations.en;

interface DashboardDailyTableProps {
  series: DashboardSeriesPoint[];
  timezone?: string;
}

const formatSeriesDate = (date: string, timezone?: string): string => {
  const [year, month, day] = date.split("-").map(Number);
  const value = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "short",
    timeZone: timezone || undefined,
  }).format(value);
};

const columns = [
  { id: "date", label: "Date" },
  { id: "orders", label: "dashboard.tripOrders" },
  { id: "standbyOrders", label: "dashboard.standbyOrders" },
  { id: "totalPaid", label: "reports.totalPaid" },
];

type DashboardSeriesTableRow = DashboardSeriesPoint & { _id: string };

const DashboardDailyTable: React.FC<DashboardDailyTableProps> = ({ series, timezone }) => {
  const { t } = useTranslation();
  const tableData = useMemo<DashboardSeriesTableRow[]>(
    () => series.map((point) => ({ ...point, _id: point.date })),
    [series],
  );

  return (
    <DetailSectionCard
      title={t("dashboard.dailyActivity" as TranslationKeys)}
      icon={<ShowChartOutlinedIcon fontSize="small" />}
      iconColor="#3B82F6"
      data-testid={TestId.DASHBOARD.DAILY_TABLE}
    >
      {series.length === 0 ? (
        <Typography color="text.secondary">
          {t("dashboard.noActivityInRange" as TranslationKeys)}
        </Typography>
      ) : (
        <Table<DashboardSeriesTableRow>
          data={tableData}
          columns={columns.map((column) => ({
            id: column.id,
            label: t(column.label as TranslationKeys),
          }))}
          showSearch={false}
          renderRow={(row) => (
            <TableRow key={row.date}>
              <TableCell>{formatSeriesDate(row.date, timezone)}</TableCell>
              <TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>{row.orders}</TableCell>
              <TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>{row.standbyOrders}</TableCell>
              <TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>
                {formatRupiah(row.totalPaid)}
              </TableCell>
            </TableRow>
          )}
        />
      )}
    </DetailSectionCard>
  );
};

export default DashboardDailyTable;
